import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Stethoscope, Users, CreditCard } from 'lucide-react';
import AdminPaymentsTab from '../components/adminTabs/AdminPaymentsTab';
import { toast } from 'react-toastify';
import {
  FEATURE_KEYS,
  LEAVE_FUTURE_LIMIT_OPTIONS,
  toggleFeatureStatus,
  setFeatureValue,
} from '../redux/featureSlice';

// Per-tab toggle config. The fekey is the source of truth — it matches the
// row in the backend Feature table and the gating call-sites in the Sidebar
// and appointment screens.
const TAB_CONFIG = {
  doctor: {
    icon: Stethoscope,
    toggles: [
      {
        fekey: FEATURE_KEYS.authDoctorRegistration,
        label: 'Doctor Registration',
        description:
          "Allow new doctors to sign up. When off, the doctor registration card and the public CTAs that lead to it are hidden across the home page and login screen.",
      },
      {
        fekey: FEATURE_KEYS.doctorAllPatientMode,
        label: 'All Patient Mode',
        description:
          "Show the “All Patients” option in the doctor's appointment modal so they can book for any patient, not just their own.",
      },
      {
        fekey: FEATURE_KEYS.doctorPatientLibrary,
        label: 'Patient Library',
        description:
          "Show the “Patient Library” item in the doctor's sidebar.",
      },
      {
        fekey: FEATURE_KEYS.medicines,
        label: 'Medicines',
        description:
          'Show the Medicines section and all medicines-related features across the app. Super-admin only.',
        superAdminOnly: true,
      },
    ],
    // Multi-value settings rendered as a stepped slider below the toggles.
    sliders: [
      {
        fekey: FEATURE_KEYS.doctorLeaveFutureLimit,
        label: 'Leave Apply Future Upto',
        description:
          'How far ahead doctors may apply for leave in the leave calendar. The leave date picker caps its selectable dates to this window. “No restriction” removes the cap.',
        options: LEAVE_FUTURE_LIMIT_OPTIONS,
        defaultValue: '12',
      },
    ],
  },
  patient: {
    icon: Users,
    toggles: [
      {
        fekey: FEATURE_KEYS.authPatientRegistration,
        label: 'Patient Registration',
        description:
          "Allow new patients to sign up. When off, the patient registration card and the public CTAs that lead to it are hidden across the home page and login screen.",
      },
      {
        fekey: FEATURE_KEYS.patientAllDoctorMode,
        label: 'All Doctor Mode',
        description:
          "Show the “All Doctors” option in the patient's appointment screen so they can browse beyond their saved doctors.",
      },
      {
        fekey: FEATURE_KEYS.patientDoctorLibrary,
        label: 'Doctor Library',
        description:
          "Show the “Doctor Library” item in the patient's sidebar.",
      },
    ],
  },
};

const ToggleSwitch = ({ checked, onChange, disabled, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    disabled={disabled}
    data-testid={testId}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-offset-2 ${
      checked ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8]' : 'bg-gray-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      aria-hidden="true"
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

// Stepped progress-bar slider for multi-value settings. Renders a filled teal
// track up to the selected stop, clickable dots at every stop, a draggable knob,
// and an invisible range input on top for pointer-drag and keyboard control.
const SegmentedSlider = ({ options, value, onChange, disabled, testId }) => {
  const found = options.findIndex((o) => o.value === value);
  const idx = found === -1 ? 0 : found;
  const lastIdx = options.length - 1;
  const pct = lastIdx === 0 ? 0 : (idx / lastIdx) * 100;

  return (
    <div className="w-full select-none" data-testid={testId}>
      <div className="relative h-6">
        {/* base track */}
        <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gray-200" />
        {/* filled progress */}
        <div
          className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition-all duration-200"
          style={{ width: `${pct}%` }}
        />
        {/* stop dots */}
        {options.map((o, i) => {
          const left = lastIdx === 0 ? 0 : (i / lastIdx) * 100;
          const reached = i <= idx;
          return (
            <button
              key={o.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(o.value)}
              data-testid={`${testId}-stop-${o.value}`}
              aria-label={o.label}
              className={`absolute top-1/2 z-10 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-white transition-colors ${
                reached ? 'border-[#4ABDBD]' : 'border-gray-300'
              } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              style={{ left: `${left}%` }}
            />
          );
        })}
        {/* knob at the selected stop */}
        <div
          className="pointer-events-none absolute top-1/2 z-20 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-md ring-2 ring-[#4ABDBD] transition-all duration-200"
          style={{ left: `${pct}%` }}
        />
        {/* invisible range for drag + keyboard */}
        <input
          type="range"
          min={0}
          max={lastIdx}
          step={1}
          value={idx}
          disabled={disabled}
          onChange={(e) => onChange(options[Number(e.target.value)].value)}
          data-testid={`${testId}-range`}
          className={`absolute inset-0 z-30 h-full w-full opacity-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        />
      </div>
      {/* stop labels */}
      <div className="relative mt-3 h-5">
        {options.map((o, i) => {
          const left = lastIdx === 0 ? 0 : (i / lastIdx) * 100;
          const active = i === idx;
          const transform =
            i === 0 ? 'translateX(0)' : i === lastIdx ? 'translateX(-100%)' : 'translateX(-50%)';
          return (
            <span
              key={o.value}
              className={`absolute top-0 whitespace-nowrap text-xs ${
                active ? 'font-semibold text-[#3AA8A8]' : 'text-gray-400'
              }`}
              style={{ left: `${left}%`, transform }}
            >
              {o.label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

const Settings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const flags = useSelector((state) => state.feature?.flags || {});
  const featureLoading = useSelector((state) => state.feature?.loading);
  const toggling = useSelector((state) => state.feature?.toggling);
  const settingValue = useSelector((state) => state.feature?.settingValue);

  const [activeTab, setActiveTab] = useState('doctor');

  // Human label for a leave-future-limit stop: "3 months" … "12 months" /
  // "No restriction" (the latter for the open-ended option).
  const sliderOptionLabel = (option) =>
    option.months === null
      ? t('settings.leaveFutureLimit.noRestriction', 'No restriction')
      : t('settings.leaveFutureLimit.months', '{{count}} months', { count: option.months });

  const handleToggle = async (fekey) => {
    const flag = flags[fekey];
    if (!flag?.featureId) {
      toast.warn(
        t(
          'settings.flagNotProvisioned',
          'This setting is not yet provisioned in the database. Re-run the seed (npm run db:seed) to create it.'
        )
      );
      return;
    }
    try {
      await dispatch(toggleFeatureStatus({ featureId: flag.featureId, fekey })).unwrap();
      toast.success(t('settings.updated', 'Setting updated'));
    } catch (err) {
      toast.error(err?.error || t('settings.updateFailed', 'Failed to update setting'));
    }
  };

  const handleSliderChange = async (fekey, value) => {
    const flag = flags[fekey];
    if (!flag?.featureId) {
      toast.warn(
        t(
          'settings.flagNotProvisioned',
          'This setting is not yet provisioned in the database. Re-run the seed (npm run db:seed) to create it.'
        )
      );
      return;
    }
    if (flag.value === value) return; // already at this stop — no-op
    try {
      await dispatch(setFeatureValue({ featureId: flag.featureId, fekey, value })).unwrap();
      toast.success(t('settings.updated', 'Setting updated'));
    } catch (err) {
      toast.error(err?.error || t('settings.updateFailed', 'Failed to update setting'));
    }
  };

  const tabs = [
    { id: 'doctor', label: t('settings.doctor', 'Doctor'), icon: Stethoscope },
    { id: 'patient', label: t('settings.patient', 'Patient'), icon: Users },
    { id: 'payments', label: t('settings.payments', 'Payments'), icon: CreditCard },
  ];

  // 'payments' is a custom tab (rendered by AdminPaymentsTab), so it has no
  // TAB_CONFIG entry — guard against undefined. superAdminOnly toggles (e.g.
  // Medicines) are hidden from plain admins.
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  const currentToggles = (TAB_CONFIG[activeTab]?.toggles || []).filter(
    (tg) => !tg.superAdminOnly || isSuperAdmin
  );
  const currentSliders = TAB_CONFIG[activeTab]?.sliders || [];

  return (
    <div className="w-full" data-testid="settings-container">
      <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
        {/* Heading */}
        <div className="flex items-center gap-3 mb-6" data-testid="settings-header">
          {/* <div className="p-2 bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] rounded-xl shadow-md">
            <Settings2 className="w-6 h-6 text-white" />
          </div> */}
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
              data-testid="settings-title"
            >
              {t('settings.title', 'Settings')}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-testid="settings-subtitle">
              {t(
                'settings.subtitle',
                'Control which features doctors and patients see across the app.'
              )}
            </p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="border-b border-gray-200 mb-6" data-testid="settings-tabs">
          <nav className="flex gap-2" data-testid="settings-tabs-nav">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  data-testid={`settings-tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                    isActive
                      ? 'border-[#4ABDBD] text-[#4ABDBD]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Payments is a custom admin tab; the doctor/patient tabs use the toggle grid */}
        {activeTab === 'payments' ? (
          <AdminPaymentsTab />
        ) : (
        <>
        {/* Toggles for the active tab */}
        <div className="rounded-2xl border border-gray-200 divide-y divide-gray-100" data-testid={`settings-toggles-list-${activeTab}`}>
          {currentToggles.map((toggle) => {
            const flag = flags[toggle.fekey];
            const checked = flag?.status === 'active';
            return (
              <div
                key={toggle.fekey}
                data-testid={`settings-toggle-row-${toggle.fekey}`}
                className="flex items-start sm:items-center justify-between gap-4 p-4 sm:p-5"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-800 text-sm sm:text-base" data-testid={`settings-toggle-label-${toggle.fekey}`}>
                    {t(`settings.toggles.${toggle.fekey}.label`, toggle.label)}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1" data-testid={`settings-toggle-description-${toggle.fekey}`}>
                    {t(`settings.toggles.${toggle.fekey}.description`, toggle.description)}
                  </p>
                </div>
                <ToggleSwitch
                  checked={checked}
                  onChange={() => handleToggle(toggle.fekey)}
                  disabled={featureLoading || toggling}
                  testId={`settings-toggle-${toggle.fekey}`}
                />
              </div>
            );
          })}
        </div>

        {/* Stepped sliders for multi-value settings on the active tab */}
        {currentSliders.length > 0 && (
          <div className="mt-6 space-y-6" data-testid={`settings-sliders-list-${activeTab}`}>
            {currentSliders.map((slider) => {
              const flag = flags[slider.fekey];
              const options = slider.options.map((o) => ({
                value: o.value,
                months: o.months,
                label: sliderOptionLabel(o),
              }));
              const current = flag?.value ?? slider.defaultValue ?? options[options.length - 1].value;
              return (
                <div
                  key={slider.fekey}
                  data-testid={`settings-slider-row-${slider.fekey}`}
                  className="rounded-2xl border border-gray-200 p-4 sm:p-5"
                >
                  <div className="mb-5 min-w-0">
                    <div
                      className="font-semibold text-gray-800 text-sm sm:text-base"
                      data-testid={`settings-slider-label-${slider.fekey}`}
                    >
                      {t(`settings.sliders.${slider.fekey}.label`, slider.label)}
                    </div>
                    <p
                      className="text-xs sm:text-sm text-gray-500 mt-1"
                      data-testid={`settings-slider-description-${slider.fekey}`}
                    >
                      {t(`settings.sliders.${slider.fekey}.description`, slider.description)}
                    </p>
                  </div>
                  <div className="px-1 sm:px-2">
                    <SegmentedSlider
                      options={options}
                      value={current}
                      onChange={(value) => handleSliderChange(slider.fekey, value)}
                      disabled={featureLoading || settingValue}
                      testId={`settings-slider-${slider.fekey}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default Settings;
